
import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Building2, 
  Search, 
  Filter, 
  Plus, 
  MoreHorizontal, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  Users, 
  Globe, 
  FileText, 
  ChevronRight,
  ShieldCheck,
  Briefcase,
  ExternalLink,
  Download,
  Share2,
  TreePine,
  User,
  GitBranch,
  XCircle,
  ArrowRight,
  Loader2,
  Zap,
  ShieldAlert,
  Building,
  Scale
} from 'lucide-react';

interface KYBEntity {
  id: string;
  name: string;
  registrationNo: string;
  country: string;
  status: 'Verified' | 'Pending' | 'Flagged';
  risk: 'Low' | 'Medium' | 'High';
  uboCount: number;
}

const MOCK_KYB: KYBEntity[] = [
  { id: 'KYB-8001', name: 'Global Logistics Ltd', registrationNo: 'UK-1029384', country: 'United Kingdom', status: 'Verified', risk: 'Low', uboCount: 3 },
  { id: 'KYB-8002', name: 'Alpha Horizon Partners', registrationNo: 'SG-993041', country: 'Singapore', status: 'Pending', risk: 'Medium', uboCount: 2 },
  { id: 'KYB-8003', name: 'Titan Shell Co.', registrationNo: 'CY-55102', country: 'Cyprus', status: 'Flagged', risk: 'High', uboCount: 1 },
  { id: 'KYB-8004', name: 'Nordic Tech NV', registrationNo: 'NL-88301', country: 'Netherlands', status: 'Verified', risk: 'Low', uboCount: 4 },
];

export const KYBView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [selectedEntity, setSelectedEntity] = useState<KYBEntity | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Form State
  const [companyName, setCompanyName] = useState('');
  const [jurisdiction, setJurisdiction] = useState('GB');
  const [ubos, setUbos] = useState([{ id: 1, name: '', share: '' }]);

  const handleAddUBO = () => {
    setUbos([...ubos, { id: Date.now(), name: '', share: '' }]);
  };

  const handleRemoveUBO = (id: number) => {
    setUbos(ubos.filter(u => u.id !== id));
  };

  const executeVerification = () => {
    setIsProcessing(true);
    // Simulate API calls to multiple registries
    setTimeout(() => {
      setIsProcessing(false);
      setIsSuccess(true);
    }, 3000);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setFormStep(1);
    setIsSuccess(false);
    setCompanyName('');
    setUbos([{ id: 1, name: '', share: '' }]);
  };

  if (selectedEntity) {
    return (
      <div className="p-6 space-y-8 animate-in slide-in-from-right duration-500 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => setSelectedEntity(null)} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-600">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">{selectedEntity.name}</h1>
              <p className="text-sm text-slate-500 font-medium uppercase tracking-widest text-[10px]">{selectedEntity.registrationNo} • Corporate Dossier</p>
            </div>
          </div>
          <div className="flex space-x-3">
             <button className="bg-white border border-slate-200 text-slate-700 p-3 rounded-2xl shadow-sm hover:bg-slate-50 transition-all"><Download size={20} /></button>
             <button className="bg-blue-600 text-white px-8 py-3 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-blue-700 shadow-xl shadow-blue-500/20 active:scale-95">Verify Structure</button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
           <div className="lg:col-span-4 space-y-6">
              <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
                 <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Entity Health</h4>
                 <div className="space-y-4">
                    <DetailItem label="Legal Status" value="Active / Registered" valueColor="text-emerald-600" />
                    <DetailItem label="Incorporation" value="12 April 2012" />
                    <DetailItem label="Jurisdiction" value={selectedEntity.country} />
                    <DetailItem label="AML Risk" value={selectedEntity.risk} valueColor={selectedEntity.risk === 'High' ? 'text-rose-600' : 'text-emerald-600'} />
                 </div>
              </div>
              <div className="bg-slate-900 p-8 rounded-[2.5rem] text-white shadow-2xl relative overflow-hidden">
                 <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4">Registry Summary</h4>
                 <p className="text-sm font-medium leading-relaxed opacity-80">
                   Verified as a legal trading entity with no known adverse media or sanction nexus detected in the last 24 hours.
                 </p>
                 <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-blue-600 rounded-full blur-[60px] opacity-10"></div>
              </div>
           </div>

           <div className="lg:col-span-8 bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm min-h-[500px]">
              <div className="flex items-center justify-between mb-12">
                 <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center">
                   <GitBranch size={16} className="mr-2 text-blue-600" /> UBO Structural Tree
                 </h3>
                 <span className="text-[10px] font-black bg-blue-50 text-blue-600 px-3 py-1 rounded-full uppercase tracking-widest border border-blue-100">{selectedEntity.uboCount} Beneficial Owners</span>
              </div>

              <div className="relative flex flex-col items-center">
                 <div className="bg-slate-900 text-white p-5 rounded-2xl shadow-xl w-64 text-center relative z-10 border-4 border-white">
                    <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Reporting Entity</p>
                    <p className="text-xs font-bold truncate">{selectedEntity.name}</p>
                 </div>

                 <div className="w-0.5 h-12 bg-slate-200"></div>
                 <div className="w-full max-w-md h-0.5 bg-slate-200 flex justify-between px-10 relative">
                    <div className="absolute top-0 left-10 w-0.5 h-8 bg-slate-200"></div>
                    <div className="absolute top-0 right-10 w-0.5 h-8 bg-slate-200"></div>
                    <div className="absolute top-0 left-1/2 w-0.5 h-8 bg-slate-200"></div>
                 </div>

                 <div className="grid grid-cols-3 gap-4 mt-8 w-full max-w-2xl">
                    <UBONode name="Julian C." share="45%" country="UK" icon={<User size={14} />} />
                    <UBONode name="BlueHoldings Ltd" share="30%" country="KY" icon={<Building2 size={14} />} />
                    <UBONode name="Public Shares" share="25%" country="GLO" icon={<Globe size={14} />} />
                 </div>
              </div>
              
              <div className="mt-16 p-6 border-t border-slate-100 bg-slate-50/50 rounded-b-[3rem]">
                 <div className="flex items-center justify-between">
                    <div className="flex -space-x-3">
                       {[1,2,3,4].map(i => (
                         <img key={i} className="w-8 h-8 rounded-full border-2 border-white shadow-sm" src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${i + 10}`} alt="User" />
                       ))}
                    </div>
                    <button className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline flex items-center">
                       Expand Ownership Network <ChevronRight size={14} className="ml-1" />
                    </button>
                 </div>
              </div>
           </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-in slide-in-from-right duration-500">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-600">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Know Your Business (KYB)</h1>
            <p className="text-sm text-slate-500 font-medium">Verify corporate structures, Ultimate Beneficial Owners (UBO), and legal status.</p>
          </div>
        </div>
        <button 
          onClick={() => setIsFormOpen(true)}
          className="bg-blue-600 text-white px-5 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest flex items-center space-x-2 hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 active:scale-95"
        >
          <Plus size={16} />
          <span>New KYB Check</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard title="Ongoing Verifications" value="12" sub="4 needing attention" icon={<Clock className="text-amber-500" />} />
        <StatCard title="Verified This Month" value="84" sub="+12% vs last month" icon={<ShieldCheck className="text-emerald-500" />} />
        <StatCard title="High Risk Alerts" value="3" sub="Requires officer sign-off" icon={<AlertCircle className="text-rose-500" />} />
      </div>

      <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center space-x-2 bg-white px-4 py-2.5 rounded-2xl border border-slate-200 w-96 shadow-inner focus-within:ring-4 focus-within:ring-blue-500/10 transition-all">
            <Search size={16} className="text-slate-400" />
            <input type="text" placeholder="Company Name, Registration, or UBO..." className="text-sm outline-none w-full font-medium" />
          </div>
          <button className="p-2.5 border border-slate-200 rounded-xl hover:bg-white hover:shadow-sm text-slate-500 transition-all">
            <Filter size={18} />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest font-black text-slate-400 border-b border-slate-100">
              <tr>
                <th className="px-6 py-5">Legal Entity</th>
                <th className="px-6 py-5">Registration</th>
                <th className="px-6 py-5">UBO Count</th>
                <th className="px-6 py-5">Risk</th>
                <th className="px-6 py-5">Status</th>
                <th className="px-6 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {MOCK_KYB.map((item) => (
                <tr key={item.id} onClick={() => setSelectedEntity(item)} className="hover:bg-slate-50 transition-all group cursor-pointer">
                  <td className="px-6 py-5">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-white group-hover:text-blue-600 transition-all shadow-sm">
                        <Building2 size={20} />
                      </div>
                      <div>
                         <p className="text-sm font-black text-slate-900 leading-tight">{item.name}</p>
                         <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest flex items-center">
                            <Globe size={10} className="mr-1" /> {item.country}
                         </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5 font-mono text-xs text-slate-500 font-semibold">{item.registrationNo}</td>
                  <td className="px-6 py-5">
                     <div className="flex items-center space-x-2 text-slate-700">
                        <Users size={14} className="text-slate-400" />
                        <span className="text-sm font-bold">{item.uboCount} Identified</span>
                     </div>
                  </td>
                  <td className="px-6 py-5">
                    <span className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase border tracking-widest ${
                      item.risk === 'High' ? 'bg-rose-50 text-rose-700 border-rose-100' : 
                      item.risk === 'Medium' ? 'bg-amber-50 text-amber-700 border-amber-100' : 
                      'bg-emerald-50 text-emerald-700 border-emerald-100'
                    }`}>
                      {item.risk} Risk
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center space-x-2">
                       {item.status === 'Verified' ? <CheckCircle2 size={16} className="text-emerald-500" /> : <Clock size={16} className="text-amber-500" />}
                       <span className="text-sm font-bold text-slate-700">{item.status}</span>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-right">
                    <button className="p-2 text-slate-400 hover:text-blue-600 transition-all hover:bg-white hover:shadow-sm rounded-xl border border-transparent hover:border-slate-100">
                      <ChevronRight size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Slide-over Registration Form */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden flex justify-end">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={closeForm}></div>
          
          <div className="relative w-full max-w-2xl bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-500 rounded-l-[3.5rem] border-l border-slate-100 overflow-hidden">
            {/* Success View */}
            {isSuccess ? (
              <div className="flex-1 flex flex-col items-center justify-center p-12 text-center space-y-8 animate-in zoom-in-95 duration-500">
                <div className="w-24 h-24 bg-emerald-100 text-emerald-600 rounded-[2.5rem] flex items-center justify-center shadow-xl shadow-emerald-100">
                   <ShieldCheck size={48} />
                </div>
                <div>
                   <h3 className="text-3xl font-black text-slate-900 tracking-tight">Onboarding Initiated</h3>
                   <p className="text-slate-500 font-medium mt-4 leading-relaxed max-w-md">
                     We{"'"}ve successfully created the corporate profile. Automated verification nodes are now scraping global registries for beneficial ownership verification.
                   </p>
                </div>
                <button 
                  onClick={closeForm}
                  className="w-full py-4 bg-slate-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl shadow-slate-200 max-w-xs"
                >
                  Return to Dashboard
                </button>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="p-10 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-blue-600 text-white rounded-2xl shadow-lg shadow-blue-200">
                      <Building2 size={24} />
                    </div>
                    <div>
                      <h2 className="text-2xl font-black text-slate-900 tracking-tight">Corporate Verification</h2>
                      <div className="flex items-center space-x-2 mt-1">
                        {[1, 2, 3].map(step => (
                          <div key={step} className={`h-1 rounded-full transition-all ${formStep >= step ? 'bg-blue-600 w-6' : 'bg-slate-200 w-3'}`}></div>
                        ))}
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-2">Step {formStep} of 3</span>
                      </div>
                    </div>
                  </div>
                  <button onClick={closeForm} className="p-3 hover:bg-white rounded-2xl text-slate-400 hover:text-slate-900 shadow-sm transition-all border border-transparent hover:border-slate-100">
                    <XCircle size={24} />
                  </button>
                </div>

                {/* Steps Content */}
                <div className="flex-1 overflow-y-auto p-10">
                  {formStep === 1 && (
                    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                      <div className="space-y-6">
                        <SectionHeader icon={<Building size={14} />} title="Entity Identification" />
                        <div className="grid grid-cols-1 gap-6">
                          <FormInput label="Company Legal Name" placeholder="Acme Global Holdings Ltd" value={companyName} onChange={setCompanyName} />
                          <div className="grid grid-cols-2 gap-6">
                             <div className="space-y-2">
                               <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Legal Jurisdiction</label>
                               <select 
                                 value={jurisdiction}
                                 onChange={(e) => setJurisdiction(e.target.value)}
                                 className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all appearance-none"
                               >
                                 <option value="GB">United Kingdom (GB)</option>
                                 <option value="SG">Singapore (SG)</option>
                                 <option value="US">United States (US)</option>
                                 <option value="DE">Germany (DE)</option>
                               </select>
                             </div>
                             <FormInput label="Registration Number" placeholder="01293485" />
                          </div>
                        </div>
                      </div>

                      <div className="p-6 bg-blue-50/50 rounded-3xl border border-blue-100 flex items-start space-x-4">
                        <div className="p-2 bg-white rounded-xl shadow-sm text-blue-600"><Zap size={18} /></div>
                        <div>
                           <p className="text-[11px] font-black text-blue-900 uppercase tracking-tight">API Auto-Complete Active</p>
                           <p className="text-xs text-blue-700 leading-relaxed mt-1">Our nodes will automatically pull articles of association and registry extracts once identification is confirmed.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {formStep === 2 && (
                    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                      <div className="space-y-6">
                        <SectionHeader icon={<FileText size={14} />} title="Registry Documentation" />
                        <div className="space-y-4">
                           <UploadSlot title="Certificate of Incorporation" desc="PDF or Scanned Image (Max 50MB)" />
                           <UploadSlot title="Articles of Association" desc="Latest verified version from public registry" />
                           <UploadSlot title="Proof of Business Address" desc="Utility bill or bank statement (< 3 months)" />
                        </div>
                      </div>
                    </div>
                  )}

                  {formStep === 3 && (
                    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <SectionHeader icon={<Users size={14} />} title="Ultimate Beneficial Owners" />
                          <button 
                            onClick={handleAddUBO}
                            className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline flex items-center"
                          >
                            <Plus size={12} className="mr-1" /> Add Person
                          </button>
                        </div>
                        
                        <div className="space-y-4">
                           {ubos.map((ubo, index) => (
                             <div key={ubo.id} className="p-6 bg-slate-50 border border-slate-200 rounded-[2rem] relative group hover:bg-white hover:border-blue-200 transition-all">
                               <div className="grid grid-cols-12 gap-4">
                                  <div className="col-span-7">
                                     <FormInput label={`UBO Name #${index + 1}`} placeholder="e.g. John Doe" />
                                  </div>
                                  <div className="col-span-4">
                                     <FormInput label="Ownership %" placeholder="25%" />
                                  </div>
                                  <div className="col-span-1 flex items-end pb-3">
                                     {ubos.length > 1 && (
                                       <button onClick={() => handleRemoveUBO(ubo.id)} className="text-slate-300 hover:text-rose-500 transition-colors">
                                          <XCircle size={18} />
                                       </button>
                                     )}
                                  </div>
                               </div>
                             </div>
                           ))}
                        </div>
                      </div>

                      <div className="p-6 bg-amber-50/50 rounded-3xl border border-amber-100 flex items-start space-x-4">
                        <div className="p-2 bg-white rounded-xl shadow-sm text-amber-600"><Scale size={18} /></div>
                        <div>
                           <p className="text-[11px] font-black text-amber-900 uppercase tracking-tight">Compliance Threshold</p>
                           <p className="text-xs text-amber-700 leading-relaxed mt-1">You must declare any individual owning {" > "} 25% directly or indirectly to satisfy AML4/5D requirements.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="p-10 border-t border-slate-100 bg-slate-50 flex space-x-4 rounded-bl-[3.5rem]">
                  {formStep > 1 && (
                    <button 
                      onClick={() => setFormStep(prev => prev - 1)}
                      className="px-8 py-4 bg-white border border-slate-200 rounded-2xl text-[11px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-100 transition-all"
                    >
                      Back
                    </button>
                  )}
                  <button 
                    onClick={() => {
                      if (formStep < 3) setFormStep(prev => prev + 1);
                      else executeVerification();
                    }}
                    disabled={formStep === 1 && !companyName}
                    className="flex-1 py-4 bg-slate-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-800 shadow-xl shadow-slate-200 transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    <span>{formStep === 3 ? 'Execute Verification' : 'Continue to Next Step'}</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              </>
            )}

            {/* Processing Overlay */}
            {isProcessing && (
              <div className="absolute inset-0 z-20 bg-white/80 backdrop-blur-md flex flex-col items-center justify-center space-y-10 animate-in fade-in duration-300">
                <div className="relative">
                  <Loader2 size={56} className="animate-spin text-blue-600" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <ShieldAlert size={18} className="text-blue-600 animate-pulse" />
                  </div>
                </div>
                <div className="text-center space-y-3">
                   <p className="text-xl font-black text-slate-900 tracking-tight uppercase">Crystal API Neural Scan</p>
                   <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] animate-pulse">Running cross-registry structural validation...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SectionHeader: React.FC<{ icon: React.ReactNode, title: string }> = ({ icon, title }) => (
  <h3 className="text-xs font-black text-blue-600 uppercase tracking-widest flex items-center">
    <div className="mr-2">{icon}</div> {title}
  </h3>
);

const FormInput: React.FC<{ label: string, placeholder: string, value?: string, onChange?: (v: string) => void }> = ({ label, placeholder, value, onChange }) => (
  <div className="space-y-2">
    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{label}</label>
    <input 
      type="text" 
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all" 
      placeholder={placeholder} 
    />
  </div>
);

const UploadSlot: React.FC<{ title: string, desc: string }> = ({ title, desc }) => (
  <div className="p-6 border-2 border-dashed border-slate-100 rounded-[2rem] flex items-center justify-between hover:border-blue-400 hover:bg-blue-50/30 transition-all cursor-pointer group">
    <div className="flex items-center space-x-4">
      <div className="p-3 bg-slate-50 rounded-2xl text-slate-400 group-hover:bg-white group-hover:text-blue-600 transition-colors shadow-inner">
         <FileText size={20} />
      </div>
      <div>
         <p className="text-xs font-black text-slate-900 leading-none mb-1">{title}</p>
         <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{desc}</p>
      </div>
    </div>
    <div className="p-2 text-slate-300 group-hover:text-blue-600">
       <Plus size={20} />
    </div>
  </div>
);

const UBONode: React.FC<{ name: string, share: string, country: string, icon: React.ReactNode }> = ({ name, share, country, icon }) => (
  <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-col items-center text-center group hover:border-blue-300 transition-all">
     <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-600 transition-all mb-3 shadow-inner">
        {icon}
     </div>
     <p className="text-xs font-black text-slate-900 leading-none mb-1">{name}</p>
     <p className="text-[10px] font-black text-blue-600 uppercase mb-2">{share} Share</p>
     <span className="text-[9px] font-black bg-slate-50 px-2 py-0.5 rounded text-slate-400 uppercase tracking-widest">{country} Registry</span>
  </div>
);

const DetailItem: React.FC<{ label: string, value: string, valueColor?: string }> = ({ label, value, valueColor = 'text-slate-900' }) => (
  <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest border-b border-slate-50 pb-2">
     <span className="text-slate-400">{label}</span>
     <span className={valueColor}>{value}</span>
  </div>
);

const StatCard: React.FC<{ title: string, value: string, sub: string, icon: React.ReactNode }> = ({ title, value, sub, icon }) => (
  <div className="bg-white p-6 rounded-[2.5rem] border border-slate-200 shadow-sm flex items-center space-x-6">
    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 shadow-inner">{icon}</div>
    <div>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{title}</p>
      <p className="text-2xl font-black text-slate-900">{value}</p>
      <p className="text-xs text-slate-500 font-medium">{sub}</p>
    </div>
  </div>
);
